import React, { useState, useEffect } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { PropTypes } from 'prop-types'
import AppLayout from '../components/layouts/AppLayout'
import SignIn from '../views/Auth/SignIn'
import SetupMfa from '../views/Auth/SetupMfa'
import { ROUTE_SETUP_MFA } from '../constants'
import { getPostAuthRedirectPath, requiresMfaSetup } from '../utils/mfa'
import Dashboard from '../views/Dashboard'
import Clients from '../views/Clients'
import ClientsDitails from '../views/Clients/Details'
import { ApplicationEvents, useApplication } from '../services/application'
import RegisteredUsers from '../views/RegisteredUsers'
import RegisteredUserDetail from '../views/RegisteredUserDetail'
import UsfcrStaff from '../views/UsfcrStaff'
import Releases from '../views/Releases'
import GlobalSettings from '../views/GlobalSettings'
import SubscriptionSettings from '../views/SubscriptionSettings'
import FormDetail from '../views/GlobalSettings/components/formDetail'
import Profile from '../views/Profile'
import UsfcrStaffDetail from '../views/UsfcrStaff/Details'
import ReleaseDetail from '../views/Releases/Details'
import AddRelease from '../views/Releases/AddRelease'
import UpdateRelease from '../views/Releases/UpdateRelease'
import RegisterNewUser from '../views/RegisteredUsers/components/RegisterNewUser'
import TooltipsManagement from '../views/TooltipsManagement'
import RequestedQuotes from '../views/RequestedQuotes'
import RequestTemplates from '../views/RequestTemplates'
import RequestTemplateDetail from '../views/RequestTemplates/Details'
import EmailTemplates from '../views/EmailTemplates'
import EmailTemplateDetails from '../views/EmailTemplates/Details'
import AdminRoles from '../views/AdminRoles'
import AdminRolesDetail from '../views/AdminRoles/Details'
import OrganizationRoleEditor from '../views/OrganizationRoleEditor'
import OrganizationRoleEditorDetail from '../views/OrganizationRoleEditor/Details'
import CourseLevels from '../views/CourseLevels'
import OrganizationLogoReview from '../views/OrganizationLogoReview'
import TeamingOpportunities from '../views/TeamingOpportunities'
import AdminActions from '../views/AdminActions'
import ResourceLinks from '../views/ResourceLinks'
import EventManagement from '../views/EventManagement'
import SubmittedForms from '../views/SubmittedForms'
import AccessDenied from '../components/generics/AccessDenied'
import ErrorBoundary from '../components/common/ErrorBoundary'

import { useSelector, useDispatch } from 'react-redux'
import { actions } from '../store/adminPortalRoles'
import { selectUserPermissions, isEnabled, selectUserRole } from '../selectors'
import useInactivityLogout from '../hooks/useInactivityLogout'

export const ROUTE_SIGNIN = { key: 'sign-in', path: 'sign-in', noAuth: true }

const notAuthOptions = {
  redirect: false ? '/dashboard' : null,
  noAuth: true,
}

const PublicRoutes = ({ children, authorized, auth }) => {
  if (!authorized) {
    return children
  }

  return <Navigate to={getPostAuthRedirectPath(auth)} replace />
}

const canAccess = (isTopLevelAdmin, permissions, ...args) =>
  Boolean(isTopLevelAdmin || isEnabled(permissions, ...args))

/** Returns the route element(s) or null — keeps <Routes> children as real Route/Fragment nodes. */
const guard = (allow, element) => (allow ? element : null)

const submittedFormsElement = (isTopLevelAdmin, permissions) =>
  canAccess(isTopLevelAdmin, permissions, 'submitted-forms') ? (
    <SubmittedForms />
  ) : (
    <AccessDenied />
  )

const PrivateAppRoutes = ({ permissions, isTopLevelAdmin }) => (
  <Routes>
    <Route path="/profile" element={<Profile />} />
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'dashboard'),
      <>
        <Route exact path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
      </>
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'organization'),
      <Route path="/organizations" element={<Clients />} />
    )}
    <Route path="/organizations/:id" element={<ClientsDitails />} />

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'registered-users'),
      <>
        <Route path="/users" element={<RegisteredUsers />} />
        <Route path="/users/:id" element={<RegisteredUserDetail />} />
        {guard(
          canAccess(isTopLevelAdmin, permissions, 'registered-users', '', 'create'),
          <Route path="/register-new-user" element={<RegisterNewUser />} />
        )}
      </>
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'usfcr-staff'),
      <Route path="/usfcr-staff" element={<UsfcrStaff />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'usfcr-staff', 'add-user'),
      <Route path="/usfcr-staff/:id" element={<UsfcrStaffDetail />} />
    )}

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'admin-actions'),
      <Route path="/admin-actions" element={<AdminActions />} />
    )}

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'releases'),
      <>
        <Route path="/releases" element={<Releases />} />
        <Route path="/releases/:id" element={<ReleaseDetail />} />
        {guard(
          canAccess(isTopLevelAdmin, permissions, 'releases', '', 'create'),
          <Route path="/releases/new" element={<AddRelease />} />
        )}
        {guard(
          canAccess(isTopLevelAdmin, permissions, 'releases', '', 'edit'),
          <Route path="/releases/update/:id" element={<UpdateRelease />} />
        )}
      </>
    )}

    {guard(
      isTopLevelAdmin,
      <>
        <Route path="/admin-portal-roles" element={<AdminRoles />} />
        <Route
          path="/admin-portal-roles/:roleKeyEdit"
          element={<AdminRolesDetail />}
        />
        <Route path="/role-editor" element={<OrganizationRoleEditor />} />
        <Route
          path="/role-editor/:roleKeyEdit"
          element={<OrganizationRoleEditorDetail />}
        />
      </>
    )}

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'global-settings'),
      <Route path="/global-settings" element={<GlobalSettings />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'event-management'),
      <Route path="/event-management" element={<EventManagement />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'resource-links'),
      <Route path="/resource-links" element={<ResourceLinks />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'subscription-settings'),
      <Route path="/subscription-settings" element={<SubscriptionSettings />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'course-levels'),
      <Route path="/course-levels" element={<CourseLevels />} />
    )}

    <Route path="/form/:id" element={<FormDetail />} />

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'tooltips-management'),
      <Route path="/tooltips-management" element={<TooltipsManagement />} />
    )}

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'requested-quotes'),
      <Route path="/requested-quotes" element={<RequestedQuotes />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'request-templates'),
      <>
        <Route path="/request-templates" element={<RequestTemplates />} />
        <Route
          path="/request-template/:id/:slug"
          element={<RequestTemplateDetail />}
        />
      </>
    )}

    {guard(
      canAccess(isTopLevelAdmin, permissions, 'email-templates'),
      <>
        <Route path="/email-templates" element={<EmailTemplates />} />
        <Route path="/email-templates/:id" element={<EmailTemplateDetails />} />
      </>
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'org-logo-review'),
      <Route path="/org-logo-review" element={<OrganizationLogoReview />} />
    )}
    {guard(
      canAccess(isTopLevelAdmin, permissions, 'teaming-opportunities'),
      <Route path="/teaming-opportunities" element={<TeamingOpportunities />} />
    )}
    <Route
      path="/submitted-forms"
      element={submittedFormsElement(isTopLevelAdmin, permissions)}
    />
  </Routes>
)

PrivateAppRoutes.propTypes = {
  permissions: PropTypes.array,
  isTopLevelAdmin: PropTypes.bool,
}

const PrivateRoutes = ({ authorized, auth }) => {
  const permissions = useSelector(selectUserPermissions)
  const userRole = useSelector(selectUserRole)

  const dispatch = useDispatch()

  React.useEffect(() => {
    if (authorized) {
      dispatch(actions.getRolesList())
    }
  }, [authorized, dispatch])

  if (!authorized) {
    return <Navigate to="/sign-in" replace />
  }

  if (requiresMfaSetup(auth)) {
    return <Navigate to={ROUTE_SETUP_MFA.path} replace />
  }

  // Top-level admin role bypasses individual permission checks.
  // This ensures admin users retain full access even before sub-role
  // permissions are loaded or if they have no sub_role_id assigned.
  const isTopLevelAdmin = userRole?.key === 'admin'

  return (
    <AppLayout>
      <ErrorBoundary>
        <PrivateAppRoutes
          permissions={permissions}
          isTopLevelAdmin={isTopLevelAdmin}
        />
      </ErrorBoundary>
    </AppLayout>
  )
}

const SetupMfaRoute = ({ authorized, auth }) => {
  if (!authorized) {
    return <Navigate to="/sign-in" replace />
  }

  if (!requiresMfaSetup(auth)) {
    return <Navigate to={getPostAuthRedirectPath(auth)} replace />
  }

  return <SetupMfa />
}

const App = () => {
  const app = useApplication()
  const auth = useSelector(state => state.auth || {})
  const [ready, setReady] = useState(false)
  const [authorized, setAuthorized] = useState(app.authorized)
  useInactivityLogout(30 * 60 * 1000);

  useEffect(() => {
    let mounted = true

    const onAuthorize = () => setAuthorized(app.authorized)
    const onLogout = () => setAuthorized(false)

    app.onReady().then(() => {
      if (!mounted) {
        return
      }

      setAuthorized(app.authorized)
      setReady(true)
    })

    app.on(ApplicationEvents.AUTHORIZE, onAuthorize)
    app.on(ApplicationEvents.LOGOUT, onLogout)
    return () => {
      mounted = false
      app.removeListener(ApplicationEvents.LOGOUT, onLogout)
      app.removeListener(ApplicationEvents.AUTHORIZE, onAuthorize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app])

  if (!ready) {
    return <></>
  }

  return (
    <>
      <Routes>
        <Route
          path="/sign-in"
          element={
            <PublicRoutes authorized={authorized} auth={auth}>
              <SignIn />
            </PublicRoutes>
          }
        />

        <Route
          path={ROUTE_SETUP_MFA.path}
          element={<SetupMfaRoute authorized={authorized} auth={auth} />}
        />

        <Route
          path="/*"
          element={<PrivateRoutes authorized={authorized} auth={auth} />}
        />
      </Routes>
    </>
  )
}

export default App